import * as React from 'react';
import { Team, ContractSeason, ContractWeek, ContractPayment } from '../../../sdk/model';
import { 
  Box, 
  Collapse, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  IconButton, 
  Typography,
  TableContainer,
  Paper
} from '@mui/material';
import { toSalary } from '../../util/SalaryDisplay';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface ContractSeasonDisplayProps {
  contractSeason: ContractSeason;
  teamMap: Map<string | undefined, Team>;
  isMobile?: boolean;
}

interface WeekWithPayments extends ContractWeek {
  payments: ContractPayment[];
}

const getRemainingGuarantee = (week: WeekWithPayments): number => {
  const paid = week.payments
    .filter(payment => payment.completed)
    .reduce((sum, payment) => sum + (payment.price || 0), 0);
  return Math.max((week.guaranteedSalary || 0) - paid, 0);
};

const getRemainingNonGuarantee = (week: WeekWithPayments): number => {
  const paid = week.payments
    .filter(payment => payment.completed)
    .reduce((sum, payment) => sum + (payment.price || 0), 0);
  return Math.max((week.fullSalary || 0) - (week.guaranteedSalary || 0) - paid, 0);
};

const ContractSeasonDisplay: React.FC<ContractSeasonDisplayProps> = ({
  contractSeason,
  teamMap,
  isMobile = false
}) => {
  const [showPayments, setShowPayments] = React.useState(false);

  const guaranteedSalary = (contractSeason.annual_salary || 0) * (contractSeason.guarantee_percentage || 0);
  
  // Calculate payment summaries
  const paid = (contractSeason.weeks || [])
    .flatMap(week => week.payments || [])
    .filter(payment => payment?.completed)
    .reduce((sum, payment) => sum + (payment?.price || 0), 0);
    
  const remaining = (contractSeason.annual_salary || 0) - paid;
  
  const remainingGuaranteed = (contractSeason.weeks || [])
    .reduce((sum, week) => sum + getRemainingGuarantee(week as WeekWithPayments), 0);
    
  const remainingNonGuaranteed = (contractSeason.weeks || [])
    .reduce((sum, week) => sum + getRemainingNonGuarantee(week as WeekWithPayments), 0);
  
  // Get unique payment teams for the season
  const paymentTeams = React.useMemo(() => {
    const paymentIds = new Set(
      (contractSeason.weeks || []).flatMap(week => 
        (week.payments || []).map(payment => 
          `${payment.teamId}-${payment.acceleratedFromYear || ''}`
        )
      )
    );
    
    return Array.from(paymentIds).map(paymentId => {
      const [teamId, year] = paymentId.split('-');
      return {
        team: teamMap.get(teamId),
        accelerated: year ? parseInt(year, 10) : undefined
      };
    });
  }, [contractSeason.weeks, teamMap]);
  
  const weeks = contractSeason.weeks || [];

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setShowPayments(!showPayments)}
            aria-label={showPayments ? 'Hide payments' : 'Show payments'}
          >
            {showPayments ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {/* season_year */}
        <TableCell>{contractSeason.season_year}</TableCell>
        {/* annual_salary */}
        <TableCell align="left">{toSalary(contractSeason.annual_salary)}</TableCell>
        {/* guaranteed_salary */}
        <TableCell align="left">{toSalary(guaranteedSalary)}</TableCell>
        {/* Paid */}
        <TableCell align="left">{toSalary(paid)}</TableCell>
        {/* Remaining */}
        <TableCell align="left">{toSalary(remaining)}</TableCell>
        {/* Rem. Guaranteed */}
        <TableCell align="left">{toSalary(remainingGuaranteed)}</TableCell>
        {/* Non-Guaranteed */}
        <TableCell align="left">{toSalary(remainingNonGuaranteed)}</TableCell>
        <TableCell>
          {paymentTeams.filter(({team}) => team?.name).map(({ team, accelerated }, index) => (
            <React.Fragment key={index}>
              {index > 0 && ', '}
              {team?.name || 'Unknown'}
              {accelerated && ` (Accel. from ${accelerated})`}
            </React.Fragment>
          ))}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={isMobile ? 2 : 8}>
          <Collapse in={showPayments} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant='h5'>{contractSeason.season_year} Payments:</Typography>
              <TableContainer component={Paper} sx={{ mt: 2, maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" aria-label="weekly breakdown">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      {Array.from(Array(17).keys()).map(weekIndex => weekIndex + 1).map(weekNum =>
                        <TableCell key={weekNum} align="center">Week {weekNum}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Full Salary Row */}
                    <TableRow>
                      <TableCell component="th" scope="row">Full</TableCell>
                      {Array.from(Array(17).keys()).map(weekIndex => weekIndex + 1).map(weekNum => {
                        const week = (contractSeason.weeks || []).find(w => w.weekNumber === weekNum);
                        return (
                          <TableCell key={weekNum} align="center">
                            {week ? toSalary(week.fullSalary) : '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* Guaranteed Salary Row */}
                    <TableRow>
                      <TableCell component="th" scope="row">Guaranteed</TableCell>
                      {Array.from(Array(17).keys()).map(weekIndex => weekIndex + 1).map(weekNum => {
                        const week = (contractSeason.weeks || []).find(w => w.weekNumber === weekNum);
                        return (
                          <TableCell key={weekNum} align="center">
                            {week ? toSalary(week.guaranteedSalary) : '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* Teams Rows */}
                    {Array.from(new Set(
                      (contractSeason.weeks || []).flatMap(week => 
                        (week.payments || []).map(payment => payment.teamId)
                      )
                    )).map(teamId => {
                      const team = teamId ? teamMap.get(teamId) : null;
                      return (
                        <TableRow key={teamId}>
                          <TableCell component="th" scope="row">
                            {team?.name || '*IR DISCOUNT*'}
                          </TableCell>
                          {Array.from(Array(17).keys()).map(weekIndex => weekIndex + 1).map(weekNum => {
                            const week = (contractSeason.weeks || []).find(w => w.weekNumber === weekNum);
                            const payment = week?.payments?.find(p => p.teamId === teamId);
                            const isComplete = payment?.completed;
                            
                            return (
                              <TableCell 
                                key={weekNum} 
                                align="center"
                                sx={{ 
                                  color: isComplete ? 'success.main' : 'inherit',
                                  fontWeight: isComplete ? 'bold' : 'normal'
                                }}
                              >
                                {payment ? toSalary(payment.price) : '-'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default ContractSeasonDisplay;